// Bicycle Interactions Animations
// 4 first-person dashcam animations covering CBR bicycle priority scenarios
// Key CBR rules: fietspad priority, 1.5m overtaking clearance, door zone, 30km/h zones

export interface BicycleAnimation {
  id: string;
  topic: string;
  sceneDescription: string;
  klingPrompt: string;
  negativePrompt: string;
  videoUrl: string | null;
  durationSeconds: number;
  viewType: 'top-down' | 'first-person' | 'third-person';
  freezeDescription: string;
  questionIds: string[];
}

export const bicycleAnimations: BicycleAnimation[] = [
  {
    id: 'bi-anim-1',
    topic: 'Bicycle Interactions',
    sceneDescription: 'Car turning right at intersection — cyclist on fietspad going straight — car must give way',
    klingPrompt:
      'Dashcam footage from inside a car in a Dutch city street approaching an intersection and turning right. ' +
      'A red bicycle lane (fietspad) crosses the exit road on the right. A cyclist is riding along the fietspad, ' +
      'going straight, clearly visible and has priority. The car slows and stops to let the cyclist pass before completing the right turn. ' +
      'Dutch urban setting: brick pavement at corners, red asphalt fietspad clearly marked, overcast daylight. ' +
      'Realistic, no text or overlays, 5 seconds.',
    negativePrompt:
      'No text, no captions, no cartoon, no aerial view, no night, no rain, no traffic lights blocking, ' +
      'no UK roads, no USA roads, no watermark',
    videoUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/animations/bi-anim-1.mp4',
    durationSeconds: 5,
    viewType: 'first-person',
    freezeDescription: 'Car at right turn — cyclist on fietspad crossing in front — car stopped to give way.',
    questionIds: ['q-bi-1', 'q-bi-2'],
  },
  {
    id: 'bi-anim-2',
    topic: 'Bicycle Interactions',
    sceneDescription: 'Car overtaking cyclist on Dutch road — safe 1.5 metre clearance — wide berth shown',
    klingPrompt:
      'Dashcam footage from inside a car driving at 50 km/h on a Dutch suburban road without a bicycle lane. ' +
      'A cyclist is riding on the right side of the road ahead. The car slows down behind the cyclist, ' +
      'waits for a clear gap in oncoming traffic, then overtakes the cyclist with a wide clearance of at least 1.5 metres, ' +
      'well to the left of the cyclist. Dutch road: straight, asphalt, white edge lines visible. Overcast daylight. ' +
      'Realistic, simple, no text or overlays, 6 seconds.',
    negativePrompt:
      'No text, no captions, no cartoon, no aerial view, no night, no rain, no bicycle lane, ' +
      'no overtaking too close, no watermark, no UK roads',
    videoUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/animations/bi-anim-2.mp4',
    durationSeconds: 6,
    viewType: 'first-person',
    freezeDescription: 'Car overtaking cyclist with clear 1.5m+ gap — wide safe pass.',
    questionIds: ['q-bi-3', 'q-bi-6', 'q-bi-8', 'q-bi-13', 'q-bi-14'],
  },
  {
    id: 'bi-anim-3',
    topic: 'Bicycle Interactions',
    sceneDescription: 'Car parked next to fietsstrook — driver about to open door — cyclist approaching in bicycle lane',
    klingPrompt:
      'Dashcam or driver-perspective view from inside a parked car on a Dutch urban street. ' +
      'A red bicycle lane (fietsstrook) runs alongside the parked car. Through the mirror and window a cyclist is visible, ' +
      'approaching from behind in the bicycle lane at normal cycling speed. ' +
      'The driver checks the mirror and waits before opening the door, showing safe door-opening behaviour. ' +
      'Dutch city street, parked cars, daytime, clear red fietsstrook markings visible. Realistic, no overlays, 4 seconds.',
    negativePrompt:
      'No text, no captions, no cartoon, no aerial view, no night, no watermark, no door already open hitting cyclist',
    videoUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/animations/bi-anim-3.mp4',
    durationSeconds: 4,
    viewType: 'first-person',
    freezeDescription: 'Driver in parked car — cyclist visible in mirror approaching — door still closed, checking before opening.',
    questionIds: ['q-bi-5'],
  },
  {
    id: 'bi-anim-4',
    topic: 'Bicycle Interactions',
    sceneDescription: 'Car following cyclist in 30 km/h residential zone — no overtaking — child cyclists present',
    klingPrompt:
      'Dashcam footage from inside a car driving slowly at 20-25 km/h in a Dutch 30 km/h residential zone (woonerf area). ' +
      'Two children on bicycles are riding on the road ahead, taking up space. The car follows at a safe distance ' +
      'without overtaking. Houses on both sides, speed bumps visible, 30 zone sign visible. Dutch residential area, ' +
      'brick road surface, parked cars, daytime. Realistic, no text or overlays, 5 seconds.',
    negativePrompt:
      'No text, no captions, no cartoon, no aerial view, no night, no rain, no overtaking, ' +
      'no watermark, no highway, no bicycle lane',
    videoUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/animations/bi-anim-4.mp4',
    durationSeconds: 5,
    viewType: 'first-person',
    freezeDescription: 'Car following children on bikes in 30km/h zone — not overtaking — keeping safe distance.',
    questionIds: ['q-bi-4', 'q-bi-10'],
  },
];
